import { useAtom } from '@reatom/npm-react'

import { AsyncStateN, pipe } from '@blade/std-lib'
import {
  basicAvailableUsdAtom,
  basicModeAtom,
  formatUsdCompact,
} from '@root/entities/basic'
import { walletsTotalsStateAtom } from '@root/entities/wallet/atoms'
import { formatLiquidityShort } from '@root/shared/core/format'

/**
 * What this account can spend, said in the units its mode trades in.
 *
 * Basic and advanced hold money in different places, and the board was reading
 * only the advanced one: on a Basic account the wallet totals never arrive —
 * no wallets request is even made — so the figure sat at `—` while the money
 * was sitting in Basic's dollar balance the whole time. Basic deliberately
 * replaces the wallet picker and the SOL readout, because showing both invites
 * the question which of the two a buy would spend.
 *
 * Returns the formatted figure, or nothing while it is still coming.
 */
export const useSpendable = (): string | undefined => {
  const [mode] = useAtom(basicModeAtom)
  const [availableUsd] = useAtom(basicAvailableUsdAtom)
  const [totalsState] = useAtom(walletsTotalsStateAtom)

  if (mode === 'basic') {
    const available = pipe(
      availableUsd,
      AsyncStateN.getOrElseW(() => undefined),
    )
    return available === undefined ? undefined : formatUsdCompact(available)
  }

  const totals = AsyncStateN.matchWaiting(totalsState, {
    Waiting: () => undefined,
    Error: () => undefined,
    Success: (value) => value,
  })

  return totals ? formatLiquidityShort(totals.usd) : undefined
}
